import { useSignal } from "@preact/signals";
import { useRef, useEffect } from "preact/hooks";
import { inputs, savedCalculations, defaultInputs } from "../state/store";
import { SavedCalculation } from "../logic/types";

export const SaveSplitButton = () => {
  const isNaming = useSignal(false);
  const name = useSignal("");
  const isDropdownOpen = useSignal(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isDropdownOpen.value &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        isDropdownOpen.value = false;
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSave = (e: Event) => {
    e.preventDefault();
    if (!name.value.trim()) return;

    const newSave: SavedCalculation = {
      id: Date.now().toString(),
      name: name.value.trim(),
      timestamp: Date.now(),
      inputs: { ...inputs.value },
    };

    savedCalculations.value = [newSave, ...savedCalculations.value];
    name.value = "";
    isNaming.value = false;
  };

  const handleRecall = (calc: SavedCalculation) => {
    inputs.value = { ...defaultInputs, ...calc.inputs };
    isDropdownOpen.value = false;
  };

  const handleDelete = (id: string, e: Event) => {
    e.stopPropagation();
    savedCalculations.value = savedCalculations.value.filter((c) => c.id !== id);
  };

  if (isNaming.value) {
    return (
      <form
        onSubmit={handleSave}
        className="absolute inset-0 flex gap-2 items-center bg-lw-surface z-10"
      >
        <input
          type="text"
          className="flex-1 min-w-0 border border-lw-border rounded-md px-2 py-1 text-sm bg-lw-surface focus:outline-none focus:ring-2 focus:ring-lw-blue/40 focus:border-lw-blue"
          placeholder="Calculation name..."
          value={name.value}
          onInput={(e) => (name.value = e.currentTarget.value)}
          autoFocus
        />
        <button
          type="submit"
          className="bg-lw-blue-deep hover:brightness-95 text-white text-sm font-medium px-3 py-1 rounded-full"
        >
          Save
        </button>
        <button
          type="button"
          className="border border-lw-border hover:bg-lw-bg text-lw-text text-sm px-3 py-1 rounded-full"
          onClick={() => (isNaming.value = false)}
        >
          Cancel
        </button>
      </form>
    );
  }

  return (
    <div className="relative inline-flex" ref={containerRef}>
      <button
        type="button"
        className="inline-flex items-center gap-1 border border-lw-blue-deep text-lw-blue-deep hover:bg-lw-blue-deep hover:text-white px-3 py-1.5 rounded-l-full text-sm font-medium"
        onClick={() => (isNaming.value = true)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          viewBox="0 0 16 16"
        >
          <path d="M2 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H9.5a1 1 0 0 0-1 1v7.293l.646-.647a.5.5 0 0 1 .708.708l-1.5 1.5a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708l.646.647V2a2 2 0 0 1 2-2H14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h2.5a.5.5 0 0 1 0 1z" />
        </svg>
        Save
      </button>
      <button
        type="button"
        className="border border-l-0 border-lw-blue-deep text-lw-blue-deep hover:bg-lw-blue-deep hover:text-white px-2 py-1.5 rounded-r-full text-sm"
        onClick={() => (isDropdownOpen.value = !isDropdownOpen.value)}
        aria-expanded={isDropdownOpen.value}
        aria-label="Toggle saved calculations"
      >
        ▾
      </button>

      {isDropdownOpen.value && (
        <ul className="absolute right-0 top-full mt-1 w-64 max-h-72 overflow-y-auto bg-lw-surface border border-lw-border rounded-xl shadow-lg z-10 py-1">
          <li className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-lw-text/60">
            Saved Calculations
          </li>
          {savedCalculations.value.length === 0 ? (
            <li className="px-3 py-2 text-sm text-lw-text/60">No saved items</li>
          ) : (
            savedCalculations.value.map((calc) => (
              <li
                key={calc.id}
                className="group relative hover:bg-lw-blue/10 transition-colors"
              >
                <button
                  className="w-full text-left px-3 py-2 pr-10"
                  type="button"
                  onClick={() => handleRecall(calc)}
                >
                  <div className="text-sm font-semibold text-lw-navy truncate">
                    {calc.name}
                  </div>
                  <div className="text-xs text-lw-text/60">
                    {new Date(calc.timestamp).toLocaleDateString()}
                  </div>
                </button>
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-lw-red opacity-0 group-hover:opacity-100 hover:scale-110 transition"
                  onClick={(e) => handleDelete(calc.id, e)}
                  title="Delete"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                    <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
                  </svg>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};
